import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';

function AdminDocuments({ user }) {
  const [documents, setDocuments] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [filterDriver, setFilterDriver] = useState('');

  useEffect(() => {
    fetchDrivers();
    fetchDocuments();
  }, [filterStatus, filterDriver]);

  const fetchDrivers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/drivers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDrivers(response.data);
    } catch (error) {
      console.error('Erreur drivers:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      let url = '/api/documents';
      const params = new URLSearchParams();

      if (filterStatus) params.append('status', filterStatus);
      if (filterDriver) params.append('driver_id', filterDriver);

      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Erreur documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyDocument = async (docId, status, notes = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/documents/${docId}/verify`, {
        status,
        notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(`Document ${status === 'approved' ? 'approuvé' : 'rejeté'}!`);
      setSelectedDocument(null);
      fetchDocuments();
    } catch (error) {
      console.error('Erreur vérification:', error);
      alert('Erreur lors de la vérification.');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f39c12',
      approved: '#27ae60',
      rejected: '#e74c3c',
      expired: '#95a5a6'
    };
    return colors[status] || '#999';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: '⏳ En attente',
      approved: '✅ Approuvé',
      rejected: '❌ Rejeté',
      expired: '⚠️ Expiré'
    };
    return labels[status] || status;
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      permis_conduire: '🎫 Permis de conduire',
      carte_vitale: '💳 Carte Vitale',
      carte_identite: '🪪 Carte d\'identité',
      attestation_securite_sociale: '🏥 Attestation Sécurité Sociale',
      rib: '🏦 RIB',
      justificatif_domicile: '🏠 Justificatif de domicile',
      carte_grise: '📋 Carte grise',
      assurance_vehicule: '🛡️ Assurance véhicule',
      controle_technique: '🔧 Contrôle technique',
      fimo_fcos: '📜 FIMO / FCOS',
      carte_chronotachygraphe: '🎴 Carte chronotachygraphe',
      autre: '📄 Autre'
    };
    return labels[type] || type;
  };

  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const days = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    return days <= 30 && days > 0;
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const pendingCount = documents.filter(d => d.status === 'pending').length;
  const expiringCount = documents.filter(d => isExpiringSoon(d.expiry_date)).length;

  if (loading) {
    return (
      <div className="container">
        <div className="loading">Chargement des documents...</div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <h2 style={{ marginBottom: '2rem' }}>📄 Gestion des Documents</h2>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
          <div style={{
            padding: '1.5rem',
            background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)',
            color: 'white',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{pendingCount}</div>
            <div style={{ fontSize: '0.9rem' }}>En attente validation</div>
          </div>

          <div style={{
            padding: '1.5rem',
            background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
            color: 'white',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              {documents.filter(d => d.status === 'approved').length}
            </div>
            <div style={{ fontSize: '0.9rem' }}>Approuvés</div>
          </div>

          <div style={{
            padding: '1.5rem',
            background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
            color: 'white',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              {documents.filter(d => d.status === 'rejected').length}
            </div>
            <div style={{ fontSize: '0.9rem' }}>Rejetés</div>
          </div>

          <div style={{
            padding: '1.5rem',
            background: 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)',
            color: 'white',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{expiringCount}</div>
            <div style={{ fontSize: '0.9rem' }}>Expirant bientôt</div>
          </div>
        </div>

        {/* Filtres */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '2rem',
          padding: '1rem',
          background: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Statut</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
            >
              <option value="">Tous</option>
              <option value="pending">En attente</option>
              <option value="approved">Approuvés</option>
              <option value="rejected">Rejetés</option>
              <option value="expired">Expirés</option>
            </select>
          </div>

          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Chauffeur</label>
            <select
              value={filterDriver}
              onChange={(e) => setFilterDriver(e.target.value)}
              style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
            >
              <option value="">Tous</option>
              {drivers.map(driver => (
                <option key={driver.id} value={driver.id}>
                  {driver.firstname} {driver.lastname} ({driver.vehicle_plate})
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={() => {
                setFilterStatus('');
                setFilterDriver('');
              }}
              style={{
                padding: '0.7rem 1.5rem',
                background: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Réinitialiser
            </button>
          </div>
        </div>

        {/* Liste des documents */}
        {documents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#999' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
            <p>Aucun document trouvé</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Chauffeur</th>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Type</th>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Nom</th>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Expiration</th>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Statut</th>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Upload</th>
                  <th style={{ padding: '1rem', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map(doc => (
                  <tr
                    key={doc.id}
                    style={{
                      borderBottom: '1px solid #e0e0e0',
                      background: doc.status === 'pending' ? '#fff3cd' : 'white',
                      cursor: 'pointer'
                    }}
                    onClick={() => setSelectedDocument(doc)}
                  >
                    <td style={{ padding: '1rem' }}>
                      <strong>{doc.driver_name}</strong>
                    </td>
                    <td style={{ padding: '1rem' }}>{getDocumentTypeLabel(doc.document_type)}</td>
                    <td style={{ padding: '1rem', fontSize: '0.9rem', color: '#666' }}>{doc.document_name}</td>
                    <td style={{ padding: '1rem' }}>
                      {doc.expiry_date ? (
                        <span style={{
                          color: isExpired(doc.expiry_date) ? '#e74c3c' : (isExpiringSoon(doc.expiry_date) ? '#f39c12' : '#666')
                        }}>
                          {format(new Date(doc.expiry_date), 'dd/MM/yyyy')}
                          {isExpiringSoon(doc.expiry_date) && ' ⚠️'}
                          {isExpired(doc.expiry_date) && ' 🚫'}
                        </span>
                      ) : (
                        <span style={{ color: '#999' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{
                        padding: '0.4rem 0.8rem',
                        background: getStatusColor(doc.status),
                        color: 'white',
                        borderRadius: '12px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold'
                      }}>
                        {getStatusLabel(doc.status)}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.85rem', color: '#999' }}>
                      {format(new Date(doc.uploaded_at), 'dd/MM/yyyy')}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      {doc.status === 'pending' && (
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVerifyDocument(doc.id, 'approved');
                            }}
                            style={{
                              padding: '0.4rem 0.8rem',
                              background: '#27ae60',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.85rem'
                            }}
                          >
                            ✅
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const notes = prompt('Raison du rejet:');
                              if (notes) handleVerifyDocument(doc.id, 'rejected', notes);
                            }}
                            style={{
                              padding: '0.4rem 0.8rem',
                              background: '#e74c3c',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.85rem'
                            }}
                          >
                            ❌
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal détails */}
        {selectedDocument && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={() => setSelectedDocument(null)}
          >
            <div
              style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '12px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflowY: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ marginBottom: '1.5rem' }}>Détails du document</h2>

              <div style={{ marginBottom: '1rem' }}>
                <strong>Chauffeur:</strong> {selectedDocument.driver_name}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Type:</strong> {getDocumentTypeLabel(selectedDocument.document_type)}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Nom:</strong> {selectedDocument.document_name}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Statut:</strong> {getStatusLabel(selectedDocument.status)}
              </div>
              {selectedDocument.expiry_date && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Date d'expiration:</strong> {format(new Date(selectedDocument.expiry_date), 'dd/MM/yyyy')}
                </div>
              )}
              <div style={{ marginBottom: '1rem' }}>
                <strong>Uploadé le:</strong> {format(new Date(selectedDocument.uploaded_at), 'dd/MM/yyyy à HH:mm')}
              </div>
              {selectedDocument.verified_at && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Vérifié le:</strong> {format(new Date(selectedDocument.verified_at), 'dd/MM/yyyy à HH:mm')}
                  <br />
                  <strong>Par:</strong> {selectedDocument.verified_by_email}
                </div>
              )}
              {selectedDocument.notes && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Notes:</strong>
                  <p style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '6px', marginTop: '0.5rem' }}>
                    {selectedDocument.notes}
                  </p>
                </div>
              )}

              {selectedDocument.file_path && (
                <div style={{ marginTop: '1.5rem' }}>
                  <a
                    href={`/api/documents/${selectedDocument.id}/download`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'block',
                      textAlign: 'center',
                      padding: '0.8rem',
                      background: '#B8472E',
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '8px',
                      fontWeight: 'bold',
                      marginBottom: '1rem'
                    }}
                  >
                    📥 Télécharger le fichier
                  </a>
                </div>
              )}

              {selectedDocument.status === 'pending' && (
                <div style={{ marginTop: '1.5rem', borderTop: '2px solid #e0e0e0', paddingTop: '1rem' }}>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                      onClick={() => handleVerifyDocument(selectedDocument.id, 'approved')}
                      style={{
                        flex: 1,
                        padding: '0.7rem',
                        background: '#27ae60',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      ✅ Approuver
                    </button>
                    <button
                      onClick={() => {
                        const notes = prompt('Raison du rejet:');
                        if (notes) handleVerifyDocument(selectedDocument.id, 'rejected', notes);
                      }}
                      style={{
                        flex: 1,
                        padding: '0.7rem',
                        background: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      ❌ Rejeter
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={() => setSelectedDocument(null)}
                style={{
                  marginTop: '1rem',
                  width: '100%',
                  padding: '0.7rem',
                  background: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDocuments;
